/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { FeatureAppLoader } from '@feature-hub/react';
import styled from 'styled-components';
import { getVideoPlayerConfig } from '../getVideoConfig';
import { UeElement } from '@oneaudi/falcon-tools';

type Asset = {
  assetFormat: string;
  videoUrlLargeViewport: string;
  videoUrlSmallViewport: string;
  ueElementProperty: string;
  ueElementLabel: string;
  alternateDescription: string;
  preserveVideoWidth?: boolean;
  loop: boolean;
  closedCaption: string;
  autoPlay: boolean;
  alternateDescriptionPortrait: string;
  closedCaptionPortrait: string;
  audioDescription: string;
  audioDescriptionPortrait: string;
};

interface VideoProps {
  asset: Asset;
  width: number;
  height: number;
}

type Options = {
  root: null;
  rootElement: null;
  threshold: [0.01];
};

export const VideoPlayer: React.FC<VideoProps> = ({ asset, width, height }) => {
  const {
    assetFormat,
    videoUrlLargeViewport,
    videoUrlSmallViewport,
    ueElementProperty,
    ueElementLabel,
    alternateDescription: alt,
    alternateDescriptionPortrait: altPortrait,
    autoPlay,
    loop,
    closedCaption: caption,
    closedCaptionPortrait: captionPortrait,
    audioDescription,
    audioDescriptionPortrait,
    preserveVideoWidth = false,
  } = asset;

  const videoPlayerWrapperRef = React.useRef<HTMLDivElement>(null);
  const serverSrcFile = process.env.IS_LEGACY ? '/fh/app.node.js' : '/fh/app-alpha.node.js';
  const clientSrcFile = process.env.IS_LEGACY ? '/fh/app.js' : '/fh/app-alpha.js';
  const videoPlayerFeatureAppBaseUrl = `https://fa-video-player.cdn.prod.collab.apps.one.audi/v5.6.0`;
  const videoPlayerFeatureAppSrc = `${videoPlayerFeatureAppBaseUrl}${clientSrcFile}`;
  const videoPlayerFeatureAppServerSrc = `${videoPlayerFeatureAppBaseUrl}${serverSrcFile}`;
  const [visible, setVisible] = React.useState(false);

  const videoPlayerConfig = useMemo(() => {
    return getVideoPlayerConfig({
      assetFormat,
      alt,
      altPortrait,
      audioDescription: audioDescription,
      audioDescriptionPortrait: audioDescriptionPortrait,
      autoPlay,
      caption,
      captionPortrait,
      loop,
      muted: false,
      videoUrlLargeScreen: videoUrlLargeViewport,
      videoUrlSmallScreen: videoUrlSmallViewport,
      width,
      preserveVideoWidth,
    });
  }, [videoUrlLargeViewport, videoUrlSmallViewport]);

  function isOnScreen(options: Options) {
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, options);

    if (videoPlayerWrapperRef.current) {
      observer.observe(videoPlayerWrapperRef.current);
    }

    return () => {
      if (videoPlayerWrapperRef.current) {
        observer.unobserve(videoPlayerWrapperRef.current);
      }
    };
  }

  React.useEffect(() => {
    return isOnScreen({
      root: null,
      rootElement: null,
      threshold: [0.01],
    });
  }, [videoPlayerWrapperRef.current]);

  return (
    <UeElement type="media" property={ueElementProperty} label={ueElementLabel}>
      <VideoPlayerWrapper ref={videoPlayerWrapperRef} height={height} className="faCarousel__video">
        {visible ? (
          <FeatureAppLoader
            config={videoPlayerConfig}
            featureAppId={`${__FEATURE_APP_NAME__}${Math.random()}`}
            serverSrc={videoPlayerFeatureAppServerSrc}
            src={videoPlayerFeatureAppSrc}
          />
        ) : null}
      </VideoPlayerWrapper>
    </UeElement>
  );
};

const VideoPlayerWrapper = styled.div<{ height: number | undefined }>`
  position: unset;
  height: ${(props) => props.height}px;
  padding: 0;
  div {
    height: ${(props) => props.height}px;
    padding-bottom: 0;
    video {
      height: ${(props) => props.height}px;
      position: unset;
    }
  }
`;
